import { useEffect, useMemo, useState, useRef } from "react";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "react-query";
import { useHistory, useLocation } from "react-router-dom";
import { ApiConstant, KeyConstant, LangConstant, SystemConstant } from "const";
import { ApiUtils, CommonUtils, HumpUtils, StorageUtils, SystemUtils } from "utils";

export default function useUserList({
  joinStatus,
  branchStatus,   // dùng cho dialog thống kê: lọc theo branch_status
  filterState,    // dùng cho dialog thống kê: lọc theo state (kết hợp với branchStatus)
  unitId,
  departmentId,
  sinceTime,
  lastTime,
  includeUnit,
  includeDepartment,
} = {}) {
  const { t: getLabel } = useTranslation();
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false; // Đánh dấu component đã unmount
    };
  }, []);

  // Lựa chọn của bộ lọc "Trạng thái hoạt động" (value "" = Tất cả, không gửi param).
  const activeFunctionOptions = [
    { value: "", label: LangConstant.TXT_FILTER_ALL },
    { value: SystemConstant.ACTIVE_FUNCTION.DISABLED, label: LangConstant.L_FN_DISABLED },
    { value: SystemConstant.ACTIVE_FUNCTION.LOGGED_IN, label: LangConstant.L_FN_LOGGED_IN },
    { value: SystemConstant.ACTIVE_FUNCTION.NOT_LOGGED_IN, label: LangConstant.L_FN_NOT_LOGGED_IN },
    { value: SystemConstant.ACTIVE_FUNCTION.SUSPENDED, label: LangConstant.L_FN_SUSPENDED },
  ];

  const maxRowPerPage = 10;

  const location = useLocation();
  const history = useHistory();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const page = searchParams.get("page") || 1;
  const activeFunction = searchParams.get("activeFunction") || "";
  const search = searchParams.get("search") || "";
  const formUnitId = searchParams.get("unitId") || 0;
  const formDepartmentId = searchParams.get("departmentId") || 0;
  const formPositionId = searchParams.get("positionId") || 0;

  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    activeFunction: activeFunction,
    search: search,
    unitId: formUnitId,
    departmentId: formDepartmentId,
    positionId: formPositionId,
  });
  const [pagination, setPagination] = useState(parseInt(page) || 1);

  const handleGetUserWithAvatar = user => {
    return { ...user, image: null };
  };

  // Dùng chung cho payload của list và export: thêm branch_status/state theo bộ lọc
  // "Trạng thái hoạt động". "Tất cả" (form.activeFunction = "") không thêm param nào.
  const applyActiveFunctionParams = payload => {
    const params = SystemConstant.ACTIVE_FUNCTION_PARAMS[form.activeFunction];
    if (params) {
      if (params.branch_status !== undefined) payload.branch_status = params.branch_status;
      if (params.state !== undefined) payload.state = params.state;
    }
    return payload;
  };

  const [serverTotal, setServerTotal] = useState(null);

  const userListQuery = useQuery({
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    queryKey: [
      ApiConstant.REACT_QUERY_KEYS.getUserList,
      pagination,
      joinStatus,
      branchStatus,
      filterState,
      form.activeFunction,
      form.search,
      form.unitId,
      form.departmentId,
      form.positionId,
      unitId,
      departmentId,
    ],
    queryFn: async () => {
      const branchId = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_ID);
      const apiPayload = HumpUtils.decamelizeKeys({
        branchId: branchId,
        page: pagination,
        limit: maxRowPerPage,
        isIncludeUnit: true,
        isIncludeDepartment: true,
        isIncludePosition: true,
      });
      if (form.search && form.search.trim() !== "") {
        apiPayload.search = form.search.trim();
      }
      applyActiveFunctionParams(apiPayload);
      if (form.unitId !== undefined && form.unitId !== null && String(form.unitId) !== "0") {
        apiPayload.unit_id = form.unitId;
      } else if (Boolean(unitId) && String(unitId) !== "0" && String(unitId) !== "-1") {
        apiPayload.unit_id = unitId;
      }
      if (form.departmentId !== undefined && form.departmentId !== null && String(form.departmentId) !== "0") {
        apiPayload.department_id = form.departmentId;
      } else if (Boolean(departmentId) && String(departmentId) !== "0") {
        apiPayload.department_id = departmentId;
      }
      if (form.positionId !== undefined && form.positionId !== null && String(form.positionId) !== "0") {
        const posVal = String(form.positionId);
        if (posVal.startsWith("pos-name:")) {
          apiPayload.position_name = posVal.replace("pos-name:", "").trim();
        } else {
          apiPayload.position_id = posVal;
        }
      }
      if (Boolean(sinceTime)) {
        apiPayload.since_time = sinceTime;
      }
      if (Boolean(lastTime)) {
        apiPayload.last_time = lastTime;
      }
      if (joinStatus !== undefined && joinStatus !== null) {
        apiPayload.account_join_status = joinStatus;
        apiPayload.state = joinStatus;
      }
      // branchStatus+filterState: lọc theo tổ hợp branch_status/state cho dialog thống kê
      if (branchStatus !== undefined && branchStatus !== null) {
        apiPayload.branch_status = branchStatus;
      }
      if (filterState !== undefined && filterState !== null) {
        apiPayload.state = filterState;
      }
      if (includeUnit !== null && includeUnit !== undefined) {
        apiPayload.is_include_unit = includeUnit;
      }
      if (includeDepartment !== null && includeDepartment !== undefined) {
        apiPayload.is_include_department = includeDepartment;
      }

      let userListResponse = await ApiUtils.createApiWithToken(false).get(ApiConstant.GET_LIST_USERS, apiPayload);
      if (isMountedRef.current) {
        userListResponse = userListResponse.data;
        if (userListResponse.status === ApiConstant.STT_OK && userListResponse.data) {
          const rawData = userListResponse.data;
          let userList = [];
          if (Array.isArray(rawData)) {
            userList = rawData;
            setServerTotal(userListResponse.total ?? userListResponse.total_users ?? null);
          } else if (rawData && typeof rawData === "object") {
            userList = rawData.list || rawData.data || rawData.users || [];
            const totalCount = userListResponse.total ?? rawData.total ?? rawData.total_users ?? userListResponse.meta?.total ?? userList.length;
            setServerTotal(totalCount);
          }
          const users = HumpUtils.camelizeKeys(userList);
          return users.map(user => handleGetUserWithAvatar(user));
        }
        throw userListResponse;
      }
      return [];
    },
    onSuccess: users => {
      if (isMountedRef.current) {
        const currentTotalUsers = users.length;
        const isRemember = StorageUtils.getStoreData(KeyConstant.KEY_REMEMBER_KEY);
        const currentBranchId = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_ID);
        const currentBranchList = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_LIST);
        const newBranchList = currentBranchList.map(branch => {
          if (branch.id !== currentBranchId) return branch;
          return { ...branch, existedAccount: currentTotalUsers };
        });
        StorageUtils.storeData(KeyConstant.KEY_BRANCH_LIST, newBranchList, isRemember);
        storageBotAccount(users);
      }
    },
    onError: error => {
      console.error(error);
      SystemUtils.appendNotification(getLabel(LangConstant.TXT_UPDATE_ERROR), "error");
    },
  });

  const exportExcel = useMutation({
    mutationFn: async () => {
      const branchId = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_ID);
      const apiPayload = HumpUtils.decamelizeKeys({
        branchId: branchId,
        isIncludeUnit: true,
        isIncludeDepartment: true,
        isIncludePosition: true,
        exportF: 1,
      });
      if (form.search && form.search.trim() !== "") {
        apiPayload.search = form.search.trim();
      }
      applyActiveFunctionParams(apiPayload);
      if (form.unitId !== undefined && form.unitId !== null && String(form.unitId) !== "0") {
        apiPayload.unit_id = form.unitId;
      } else if (Boolean(unitId) && String(unitId) !== "0" && String(unitId) !== "-1") {
        apiPayload.unit_id = unitId;
      }
      if (form.departmentId !== undefined && form.departmentId !== null && String(form.departmentId) !== "0") {
        apiPayload.department_id = form.departmentId;
      } else if (Boolean(departmentId) && String(departmentId) !== "0") {
        apiPayload.department_id = departmentId;
      }
      if (form.positionId !== undefined && form.positionId !== null && String(form.positionId) !== "0") {
        const posVal = String(form.positionId);
        if (posVal.startsWith("pos-name:")) {
          apiPayload.position_name = posVal.replace("pos-name:", "").trim();
        } else {
          apiPayload.position_id = posVal;
        }
      }
      if (Boolean(sinceTime)) {
        apiPayload.since_time = sinceTime;
      }
      if (Boolean(lastTime)) {
        apiPayload.last_time = lastTime;
      }
      if (joinStatus !== undefined && joinStatus !== null) {
        apiPayload.account_join_status = joinStatus;
        apiPayload.state = joinStatus;
      }
      // branchStatus+filterState: lọc theo tổ hợp branch_status/state cho dialog thống kê
      if (branchStatus !== undefined && branchStatus !== null) {
        apiPayload.branch_status = branchStatus;
      }
      if (filterState !== undefined && filterState !== null) {
        apiPayload.state = filterState;
      }
      if (includeUnit !== null && includeUnit !== undefined) {
        apiPayload.is_include_unit = includeUnit;
      }
      if (includeDepartment !== null && includeDepartment !== undefined) {
        apiPayload.is_include_department = includeDepartment;
      }

      const response = await ApiUtils.createApiWithToken(false, { ...ApiUtils.API_CONFIG, responseType: "blob" }).get(
        ApiConstant.GET_LIST_USERS,
        HumpUtils.decamelizeKeys(apiPayload),
      );
      return URL.createObjectURL(response.data);
    },
    onMutate: () => {
      SystemUtils.openCircleLoading();
    },
    onSettled: () => {
      SystemUtils.openCircleLoading(false);
    },
    onSuccess: async response => { },
    onError: error => {
      console.log(error);
      SystemUtils.appendNotification(getLabel(LangConstant.TXT_UPDATE_ERROR), "error");
    },
  });

  const handleChangeActiveFunction = event => {
    const value = event.target.value;
    const newUrlSearchParams = new URLSearchParams(searchParams);
    newUrlSearchParams.set("page", "1");
    if (value) {
      newUrlSearchParams.set("activeFunction", value);
    } else {
      newUrlSearchParams.delete("activeFunction");
    }
    history.push(location.pathname + "?" + newUrlSearchParams.toString());
    setForm(form => ({ ...form, activeFunction: value }));
    setPagination(1);
  };

  const handleChangeSearchInput = event => {
    const newUrlSearchParams = new URLSearchParams(searchParams);
    newUrlSearchParams.set("page", "1");
    newUrlSearchParams.set("search", event.target.value);
    history.push(location.pathname + "?" + newUrlSearchParams.toString());
    setForm(form => ({ ...form, search: event.target.value }));
    setPagination(1);
  };

  const handleChangeUnitDropdown = async event => {
    const newUrlSearchParams = new URLSearchParams(searchParams);
    newUrlSearchParams.set("page", "1");
    newUrlSearchParams.set("unitId", event.target.value);
    history.push(location.pathname + "?" + newUrlSearchParams.toString());
    setForm(form => ({ ...form, unitId: event.target.value, departmentId: 0 }));
    setPagination(1);
  };

  const handleChangeDepartmentDropdown = async event => {
    const newUrlSearchParams = new URLSearchParams(searchParams);
    newUrlSearchParams.set("page", "1");
    newUrlSearchParams.set("departmentId", event.target.value);
    history.push(location.pathname + "?" + newUrlSearchParams.toString());
    setForm(form => ({ ...form, departmentId: event.target.value }));
    setPagination(1);
  };

  const handleChangePositionDropdown = async event => {
    const newUrlSearchParams = new URLSearchParams(searchParams);
    newUrlSearchParams.set("page", "1");
    newUrlSearchParams.set("positionId", event.target.value);
    history.push(location.pathname + "?" + newUrlSearchParams.toString());
    setForm(form => ({ ...form, positionId: event.target.value }));
    setPagination(1);
  };

  const handleChangePage = (eventOrPage, pageNumber) => {
    const targetPage = pageNumber !== undefined ? pageNumber : eventOrPage;
    const finalPage = parseInt(targetPage) || 1;
    const newUrlSearchParams = new URLSearchParams(searchParams);
    newUrlSearchParams.set("page", finalPage);
    history.push(location.pathname + "?" + newUrlSearchParams.toString());
    setPagination(finalPage);
  };

  const handleChangePageWithStatusActive = (_, page) => {
    const newUrlSearchParams = new URLSearchParams(searchParams);
    newUrlSearchParams.set("page", page);
    setPagination(page);
  };

  const filterUsers = useMemo(() => {
    if (serverTotal !== null && serverTotal !== undefined) {
      return users;
    }
    return users.filter(user => {
      // Bộ lọc "Trạng thái hoạt động" (fallback client-side). "Tất cả" (form.activeFunction = "")
      // không lọc gì; ngược lại chỉ giữ bản ghi có cùng nhãn với lựa chọn.
      let activeFunctionCondition = true;
      if (form.activeFunction) {
        activeFunctionCondition = SystemConstant.resolveActiveFunction(user) === form.activeFunction;
      }

      let unitCondition = false;
      if (form.unitId === 0) {
        unitCondition = true;
      } else if (form.unitId === -1) {
        if (!user.unitId) unitCondition = true;
      } else {
        if (user.unitId === form.unitId) unitCondition = true;
      }

      let departmentCondition = false;
      if (form.departmentId === 0) {
        departmentCondition = true;
      } else if (form.departmentId === -1) {
        if (!user.departmentId) departmentCondition = true;
      } else {
        if (user.departmentId === form.departmentId) departmentCondition = true;
      }

      let positionCondition = false;
      if (!form.positionId || String(form.positionId) === "0") {
        positionCondition = true;
      } else if (String(form.positionId).startsWith("pos-name:")) {
        const reqName = String(form.positionId).replace("pos-name:", "").trim().toLowerCase();
        const userPosName = user.positionName ? user.positionName.trim().toLowerCase() : "";
        if (userPosName === reqName) positionCondition = true;
      } else if (form.positionId === -1) {
        if (!user.positionId) positionCondition = true;
      } else {
        if (user.positionId === form.positionId) positionCondition = true;
      }

      const searchValue = CommonUtils.removeVietnameseTones(form.search.toLowerCase());
      const userName = CommonUtils.removeVietnameseTones(user.name?.toLowerCase() || "");
      const phone = user.phone || "";
      const phoneWithoutVNCountryCode = phone.replace("+84", "0");

      return (
        activeFunctionCondition &&
        unitCondition &&
        departmentCondition &&
        positionCondition &&
        Boolean(
          !CommonUtils.isJson(user.name) &&
          (searchValue === "" ||
            userName.includes(searchValue) ||
            phone.includes(searchValue) ||
            phoneWithoutVNCountryCode.includes(searchValue)),
        )
      );
    });
  }, [users, form.activeFunction, form.unitId, form.departmentId, form.positionId, form.search, serverTotal]);

  const currentPageUsers = useMemo(() => {
    if (serverTotal !== null && serverTotal !== undefined) {
      return users;
    }
    return CommonUtils.paginateFromArray(filterUsers, pagination, maxRowPerPage);
  }, [users, filterUsers, pagination, serverTotal, maxRowPerPage]);

  const storageBotAccount = users => {
    users.forEach(data => {
      if (data.phone === SystemConstant.BOT_ACCOUNT_PHONE) {
        StorageUtils.storeData(KeyConstant.KEY_BOT_ACCOUNT_ID, data.accountId, true);
      }
    });
  };

  const effectiveTotal = serverTotal !== null && serverTotal !== undefined ? serverTotal : filterUsers.length;

  const paginationConfigs = {
    current: pagination,
    total: Math.ceil(effectiveTotal / maxRowPerPage),
    total_users: effectiveTotal,
    serverTotal: effectiveTotal,
  };

  const funcExportExcel = async () => {
    return exportExcel.mutateAsync();
  };

  useEffect(() => {
    if (userListQuery.data) setUsers(userListQuery.data);
  }, [userListQuery.data]);

  return {
    form: form,
    setForm: setForm,
    activeFunctionOptions: activeFunctionOptions,
    pagination: paginationConfigs,
    totalUsers: users,
    filterUsers: filterUsers,
    currentPageUsers: currentPageUsers,
    isLoading: userListQuery.isLoading,
    handleChangeActiveFunction: handleChangeActiveFunction,
    handleChangePageWithStatusActive,
    handleChangeUnitDropdown: handleChangeUnitDropdown,
    handleChangeDepartmentDropdown: handleChangeDepartmentDropdown,
    handleChangePositionDropdown: handleChangePositionDropdown,
    handleChangeSearchInput: debounce(handleChangeSearchInput, 500),
    handleChangePage: handleChangePage,
    funcExportExcel,
  };
}
